import styled from 'styled-components';
import type { DefaultTheme } from 'styled-components';

/**
 * Callout — a bordered aside inside a reading column (policies, help, the
 * legal-floor form). One treatment, three tones, so the same kind of box does
 * not arrive in three different costumes across four pages.
 *
 *   info    the default. A pointer to something else — neutral border, muted
 *           text. Deliberately NOT the danger hue: a box that says "you can
 *           report unlawful content here" is informational, and painting it red
 *           on the Privacy Policy claims a danger that is not on the page.
 *   urgent  the emergency-services line. Red, because it is the one thing on
 *           the page someone must not scroll past.
 *   danger  a failed action (a form error). Red border, body-coloured text so
 *           the message itself stays the most legible thing in the box.
 *
 * Glass-aware in BOTH halves, which is the part hand-rolled callouts keep
 * missing: `glassBg` is ~72% opaque, so a translucent fill with no
 * `backdrop-filter` lets a quarter of the animated backdrop through unblurred
 * and the text fights it. Fill and blur are set together here, exactly as
 * ConsolePanel/RailGroup pair them, and glass OFF is the fully opaque `surface`
 * with no filter at all.
 */
export type CalloutTone = 'info' | 'urgent' | 'danger';

const toneColor = (tone: CalloutTone, theme: DefaultTheme): string | null => {
  if (tone === 'urgent') return theme.color.safety;
  if (tone === 'danger') return theme.color.danger;
  return null;
};

const borderFor = (tone: CalloutTone, theme: DefaultTheme): string =>
  toneColor(tone, theme) ? 'transparent' : theme.color.glassBorder;

const fillFor = (tone: CalloutTone, theme: DefaultTheme): string => {
  const base = theme.cardsGlass ? theme.color.glassBg : theme.color.surface;
  const hue = toneColor(tone, theme);
  return hue ? `color-mix(in srgb, ${hue} 14%, ${base})` : base;
};

const textFor = (tone: CalloutTone, theme: DefaultTheme): string => {
  if (tone === 'urgent') return theme.color.safety;
  if (tone === 'danger') return theme.color.textPrimary;
  return theme.color.textMuted;
};

const glassFilter = (blur: number, sat: number): string => `blur(${blur}px) saturate(${sat})`;

export const Callout = styled.p<{ $tone?: CalloutTone }>`
  margin: 0;
  padding: 12px 16px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme, $tone = 'info' }) => borderFor($tone, theme)};
  /* LONGHAND: the \`background\` shorthand would wipe any inherited image layer
     (same rule ConsolePanel documents). */
  background-color: ${({ theme, $tone = 'info' }) => fillFor($tone, theme)};
  backdrop-filter: ${({ theme }) =>
    theme.cardsGlass ? glassFilter(theme.glassBlur, theme.glassSaturation) : 'none'};
  -webkit-backdrop-filter: ${({ theme }) =>
    theme.cardsGlass ? glassFilter(theme.glassBlur, theme.glassSaturation) : 'none'};
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: 1.55;
  color: ${({ theme, $tone = 'info' }) => textFor($tone, theme)};
`;
